import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { db } from '../utils/firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { MagazineContent, Product, LookbookImage } from '../types';
import { Calendar, User, ArrowLeft } from 'lucide-react';
import ProductCard from './ProductCard';

const MagazineDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [content, setContent] = useState<MagazineContent | null>(null);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      if (!id) return;

      setLoading(true);
      try {
        const docRef = doc(db, 'magazine', id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const contentData = { id: docSnap.id, ...docSnap.data() } as MagazineContent;
          setContent(contentData);

          // Collect all product IDs (global + per-image for lookbooks)
          const allProductIds = new Set<string>();

          if (contentData.productIds) {
            contentData.productIds.forEach(pid => allProductIds.add(pid));
          }

          if ((contentData.type === 'lookbook' || contentData.type === 'runway') && contentData.lookbookImages) {
            contentData.lookbookImages.forEach(img => {
              if (img.productIds) {
                img.productIds.forEach(pid => allProductIds.add(pid));
              }
            });
          }

          // Fetch all products
          if (allProductIds.size > 0) {
            const productPromises = Array.from(allProductIds).map(pid => {
              return getDocs(query(collection(db, 'products'), where('__name__', '==', pid)));
            });

            const productSnaps = await Promise.all(productPromises);
            const fetchedProducts = productSnaps
              .map(snap => !snap.empty ? { id: snap.docs[0].id, ...snap.docs[0].data() } as Product : null)
              .filter(p => p !== null) as Product[];

            setAllProducts(fetchedProducts);
          }
        }
      } catch (error) {
        console.error("Error fetching magazine content:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [id]);

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'lookbook': return 'Lookbook';
      case 'editorial': return 'Editorial';
      case 'campaign': return 'Campaign';
      case 'runway': return 'Runway';
      default: return type;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'lookbook': return 'bg-blue-600';
      case 'editorial': return 'bg-purple-600';
      case 'campaign': return 'bg-red-600';
      case 'runway': return 'bg-black';
      default: return 'bg-gray-600';
    }
  };

  const getProductsForImage = (productIds?: string[]): Product[] => {
    if (!productIds || productIds.length === 0) return [];
    return allProducts.filter(p => productIds.includes(p.id));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen pt-32 px-4 text-center">
        <h1 className="font-heading text-3xl font-bold uppercase mb-4">Content Not Found</h1>
        <Link to="/magazine" className="inline-block mt-8 bg-black text-white px-8 py-3 font-heading font-bold uppercase tracking-widest text-xs hover:bg-gray-800 transition-colors">
          Back to Magazine
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb Navigation */}
      <div className="max-w-4xl mx-auto px-4 pt-28 pb-8">
        <Link to="/magazine" className="inline-flex items-center gap-2 text-xs font-heading uppercase tracking-wider text-gray-500 hover:text-black transition-colors">
          <ArrowLeft size={14} />
          Magazine
        </Link>
      </div>

      {/* Header - Minimal Style */}
      <div className="max-w-4xl mx-auto px-4 pb-12">
        {/* Title */}
        <h1 className="text-3xl md:text-4xl font-heading font-bold mb-6 leading-tight">
          {content.title}
        </h1>

        {/* Metadata */}
        <div className="mb-8 text-xs text-gray-500 uppercase tracking-wider flex items-center gap-4 flex-wrap">
          <span className="text-black font-bold">{getTypeLabel(content.type)}</span>
          {content.season && <span>{content.season}</span>}
          <span className="flex items-center gap-1">
            <Calendar size={12} />
            {formatDate(content.publishDate)}
          </span>
          {content.author && (
            <span className="flex items-center gap-1">
              <User size={12} />
              {content.author}
            </span>
          )}
        </div>

        {/* Cover Image */}
        <div className="w-full mb-8 bg-gray-100">
           <img
             src={content.coverImage || '/assets/placeholder.svg'}
             alt={content.title}
             onError={(e) => {
               const t = e.currentTarget;
               if (!t.dataset.fallback) { t.dataset.fallback = '1'; t.src = '/assets/placeholder.svg'; }
             }}
             className="w-full h-auto object-cover"
           />
        </div>

        {/* Subtitle */}
        {content.subtitle && (
          <p className="text-lg font-body text-gray-700 mb-6">{content.subtitle}</p>
        )}

        {/* Description */}
        <div className="font-body text-gray-600 leading-relaxed mb-8">
          <p>{content.description}</p>
        </div>
      </div>

      {/* LOOKBOOK FORMAT: Sequential full-width images with minimal captions */}
      {(content.type === 'lookbook' || content.type === 'runway') && content.lookbookImages && content.lookbookImages.length > 0 && (
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="space-y-12">
            {content.lookbookImages.map((lookbookImg: LookbookImage, idx: number) => {
              const imageProducts = getProductsForImage(lookbookImg.productIds);

              return (
                <div key={idx} className="mb-16">
                  {/* Full-width Image */}
                  <div className="w-full mb-6 bg-gray-100">
                    <img
                      src={lookbookImg.url || '/assets/placeholder.svg'}
                      alt={`Look ${idx + 1}`}
                      onError={(e) => {
                        const t = e.currentTarget;
                        if (!t.dataset.fallback) { t.dataset.fallback = '1'; t.src = '/assets/placeholder.svg'; }
                      }}
                      className="w-full h-auto object-cover"
                    />
                  </div>

                  {/* Image Caption/Description */}
                  {lookbookImg.description && (
                    <div className="text-sm font-body text-gray-600 leading-relaxed mb-4">
                      <p>{lookbookImg.description}</p>
                    </div>
                  )}

                  {/* Shop This Look - Horizontal Thumbnail List */}
                  {imageProducts.length > 0 && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <h3 className="text-xs font-heading uppercase tracking-wider text-gray-500 mb-4">
                        Shop This Look
                      </h3>
                      <div className="flex flex-col gap-4">
                        {imageProducts.map(product => (
                          <div 
                            key={product.id} 
                            onClick={() => navigate(`/product/${product.id}`)}
                            className="flex items-center gap-4 cursor-pointer group"
                          >
                            <img
                              src={product.image || '/assets/placeholder.svg'}
                              alt={product.title}
                              onError={(e) => {
                                const t = e.currentTarget;
                                if (!t.dataset.fallback) { t.dataset.fallback = '1'; t.src = '/assets/placeholder.svg'; }
                              }}
                              className="w-16 h-20 object-cover bg-gray-100"
                            />
                            <div className="flex items-baseline gap-2">
                               <h4 className="font-heading font-bold text-sm uppercase group-hover:text-gray-600 transition-colors">
                                 {product.title}
                               </h4>
                               <span className="text-xs text-gray-300">|</span>
                               <p className="font-heading font-medium text-sm">
                                 £{product.price.toFixed(2)}
                               </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* EDITORIAL/CAMPAIGN FORMAT: Sequential gallery */}
      {(content.type !== 'lookbook' && content.type !== 'runway') && content.images && content.images.length > 0 && (
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="space-y-8">
            {content.images.map((img, idx) => (
              <div key={idx} className="w-full bg-gray-100">
                <img
                  src={img || '/assets/placeholder.svg'}
                  alt={`${content.title} - Image ${idx + 1}`}
                  onError={(e) => {
                    const t = e.currentTarget;
                    if (!t.dataset.fallback) { t.dataset.fallback = '1'; t.src = '/assets/placeholder.svg'; }
                  }}
                  className="w-full h-auto object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Global Products (for non-lookbook or additional products) */}
      {(content.type !== 'lookbook' && content.type !== 'runway') && allProducts.length > 0 && (
        <div className="max-w-4xl mx-auto px-4 py-12 mt-12 border-t border-gray-200">
          <h2 className="text-xs font-heading uppercase tracking-wider text-gray-500 mb-6">
            Shop The Collection
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {allProducts.map(product => (
              <ProductCard 
                key={product.id} 
                product={product} 
                onClick={(p) => navigate(`/product/${p.id}`)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


export default MagazineDetail;
